import type { DensityConfigOverrides } from './build-config';
import type { RandomDotStudioPattern } from './randomize-pattern';
import { type DotStudioMode, isDotStudioMode } from './types';

export const DOT_STUDIO_HERO_MARKER_PREFIX = 'data:image/svg+xml;base64,';
const MARKER_TAG = 'morfi-dot-studio-v1';

interface MarkerPayload {
  readonly v: 1;
  readonly variant: DotStudioMode;
  readonly seed: string;
  readonly o: DensityConfigOverrides;
}

function encodeBase64Utf8(text: string): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(text, 'utf8').toString('base64');
  }
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function decodeBase64Utf8(base64: string): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(base64, 'base64').toString('utf8');
  }
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Persist a Dot Studio hero choice through `users.hero_url` without IPFS or
 * backend schema changes. The API already accepts tiny `data:image/svg+xml`
 * preset URLs; this marker SVG stores variant + seed + overrides in metadata.
 */
export function encodeDotStudioHeroMarker(pattern: RandomDotStudioPattern): string {
  const payload: MarkerPayload = {
    v: 1,
    variant: pattern.variant,
    seed: pattern.seedText,
    o: pattern.overrides,
  };
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1">` +
    `<metadata>${MARKER_TAG}:${JSON.stringify(payload)}</metadata></svg>`;
  return `${DOT_STUDIO_HERO_MARKER_PREFIX}${encodeBase64Utf8(svg)}`;
}

export function parseDotStudioHeroMarker(
  heroUrl: string | null | undefined,
): RandomDotStudioPattern | null {
  if (typeof heroUrl !== 'string' || !heroUrl.startsWith(DOT_STUDIO_HERO_MARKER_PREFIX)) {
    return null;
  }
  try {
    const svg = decodeBase64Utf8(heroUrl.slice(DOT_STUDIO_HERO_MARKER_PREFIX.length));
    const start = svg.indexOf(`${MARKER_TAG}:`);
    if (start === -1) return null;
    const jsonStart = start + MARKER_TAG.length + 1;
    const jsonEnd = svg.indexOf('</metadata>', jsonStart);
    if (jsonEnd === -1) return null;
    const payload = JSON.parse(svg.slice(jsonStart, jsonEnd)) as MarkerPayload;
    if (
      payload.v !== 1 ||
      typeof payload.variant !== 'string' ||
      typeof payload.seed !== 'string' ||
      !isDotStudioMode(payload.variant)
    ) {
      return null;
    }
    return {
      variant: payload.variant,
      seedText: payload.seed,
      overrides: payload.o ?? {},
    };
  } catch {
    // SILENT-CATCH: malformed marker payloads are treated as absent; callers fall back to default cover.
    return null;
  }
}

/**
 * Dot Studio marker / no cover → client-rendered cover; any real photo URL
 * (including IPFS-hosted uploads) → passthrough.
 *
 * This previously also mapped `images.morfi.markets/ipfs/` URLs to `dot-studio`
 * so legacy baked generative covers re-rendered via the live engine. But photo
 * cover uploads ALSO land on that Pinata gateway, so the rule painted a
 * freshly-uploaded cover as the blue synthesized pattern instead of the photo
 * (MOR-831). Uploads now passthrough; a dead legacy pin still falls back to the
 * deterministic pattern via OptimizedImage's broken-upload handling.
 */
export function heroCoverDisplayMode(
  heroUrl: string | null | undefined,
): 'dot-studio' | 'passthrough' {
  if (heroUrl === null || heroUrl === undefined || heroUrl.length === 0) {
    return 'dot-studio';
  }
  if (heroUrl.startsWith(DOT_STUDIO_HERO_MARKER_PREFIX)) {
    return 'dot-studio';
  }
  return 'passthrough';
}
